import React from 'react';
import { Box, Container, Typography, Grid, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const scrollToAbout = () => {
    const element = document.getElementById('about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box
      sx={{
        minHeight: { xs: '70vh', md: '80vh' },
        paddingTop: { xs: '64px', md: '80px' },
        background: 'linear-gradient(90deg, #0968E5, #091970)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at 20% 50%, rgba(9, 104, 229, 0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', alignItems: 'center' }}>
        <Grid container spacing={4} alignItems="center">
          {/* Text Content - Left Side */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                color: 'white',
                textAlign: { xs: 'center', md: 'left' },
                py: { xs: 2, md: 0 },
              }}
            >
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Typography
                  variant={isMobile ? 'h3' : isTablet ? 'h2' : 'h1'}
                  sx={{
                    fontWeight: 700,
                    mb: { xs: 1.5, sm: 2 },
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    letterSpacing: { xs: '0.5px', md: '1px' },
                    fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem', lg: '3.5rem' },
                  }}
                >
                  AJAY JHUNJHUNWALA
                </Typography>
              </motion.div>

              <motion.div
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Typography
                  variant={isMobile ? 'h6' : isTablet ? 'h5' : 'h4'}
                  sx={{
                    fontWeight: 400,
                    mb: { xs: 2, sm: 2.5, md: 3 },
                    textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                    letterSpacing: { xs: '0.5px', md: '1px' },
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.5rem', lg: '1.75rem' },
                  }}
                >
                  HEAD OF SALES & MARKETING
                </Typography>
              </motion.div>

              <motion.div
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem', lg: '1.25rem' },
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    lineHeight: { xs: 1.6, md: 1.8 },
                    px: { xs: 2, md: 0 },
                    mb: { xs: 1.5, md: 2 },
                  }}
                >
                  Transforming Markets, Building Relationships, Driving Growth
                </Typography>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem', lg: '1.1rem' },
                    fontWeight: 400,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                    lineHeight: { xs: 1.6, md: 1.8 },
                    px: { xs: 2, md: 0 },
                  }}
                >
                  17+ Years of Excellence in Business Development & Strategic Marketing
                </Typography>
              </motion.div>
            </Box>
          </Grid>

          {/* Image - Right Side */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', md: 'flex-end' },
                alignItems: 'center',
              }}
            >
              <motion.div
                initial={{ scale: 0, opacity: 0, x: 50 }}
                animate={{ scale: 1, opacity: 1, x: 0 }}
                transition={{ duration: 0.8, type: 'spring', delay: 0.3 }}
              >
                <Box
                  sx={{
                    width: { xs: 200, sm: 250, md: 300 },
                    height: { xs: 200, sm: 250, md: 300 },
                    borderRadius: '50%',
                    border: '4px solid rgba(9, 104, 229, 0.5)',
                    padding: '4px',
                    background: 'linear-gradient(135deg, rgba(9, 104, 229, 0.3), rgba(9, 25, 112, 0.2))',
                    boxShadow: '0 15px 40px rgba(9, 104, 229, 0.4)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src="/images/ajay.jpeg"
                    alt="Ajay Jhunjhunwala"
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Box>
              </motion.div>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Floating Scroll Down Indicator */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 20, md: 30 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      >
        <motion.div
          animate={{
            y: [0, 10, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <IconButton
            onClick={scrollToAbout}
            sx={{
              color: 'white',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              width: { xs: 48, md: 56 },
              height: { xs: 48, md: 56 },
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.5)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: { xs: 32, md: 40 } }} />
          </IconButton>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Hero;

